import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import Headshot from '@mlb/react-patterns/components/Headshot';
import Skeleton from '@mlb/react-patterns/components/Skeleton';
import IconPattern from '@mlb/react-patterns/components/Icon';
import COLORS from '@mlb/react-patterns/style/universal.color';

import AspectRatioCard, { Container } from '../AspectRatioCard';
import ScalableText from '../ScalableText';
import { useDictionary } from '../../hooks/config';

export const BASE_WIDTH = 109;
export const BASE_HEIGHT = 125;
const ICON_WIDTH = 27.5;
const CIRCLE = 64;
const GAP = 8;

const Card = styled(AspectRatioCard)`
    background: radial-gradient(49.03% 62.36% at 51.15% 50.97%, #333333 0%, #121212 100%);

    ${Container} {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        row-gap: ${GAP * 100 / BASE_WIDTH}%;
    }
`;

const Text = styled(ScalableText)`
    font-weight: 600;
    text-align: center;
    line-height: 18px;
    box-sizing: border-box;
    padding: 0 ${GAP}px;
    max-width: 140px;
`;

const RoundedWrapper = styled.div`
    width: ${CIRCLE * 100 / BASE_WIDTH}%;
    height: ${CIRCLE * 100 / BASE_HEIGHT}%;
    background: linear-gradient(0deg, rgba(255, 255, 255, 0.14), rgba(255, 255, 255, 0.14)), #121212;
    border-radius: 50%;
    overflow: hidden;
    position: relative;
`;

const Icon = styled(IconPattern)`
    width: ${ICON_WIDTH * 100 / CIRCLE}%;
    height: ${ICON_WIDTH * 100 / CIRCLE}%;
    margin: ${(100 - ICON_WIDTH * 100 / CIRCLE) / 2}%;
    &:not(:root),
    symbol {
        overflow: visible;
    }
    path, use {
        stroke: ${COLORS.mlbWhite};
        stroke-width: 5%;
    }
`;

const P = styled.p`
    margin: 0;
    padding: 0;
`;

const PlayerCard = (props) => {

    const {
        id,
        lastName,
        name,
        loading,
        as,
        ...otherProps
    } = props;

    const {
        edit_teams = 'Edit Teams',
        and_players = '& Players',
    } = useDictionary();

    const [
        firstName,
        ...suffix
    ] = name.split(lastName);

    return (
        <Card
            aspectRatio={`${BASE_WIDTH}:${BASE_HEIGHT}`}
            forwardedAs={as}
            {...otherProps}
        >
            { loading ? <Skeleton margin={0} /> : (
                <>
                    {id ? (
                        <Headshot
                            playerId={id}
                            type="spots"
                            width={`${CIRCLE * 100 / BASE_WIDTH}%`}
                            ratio="1:1"
                            zoomScale={1}
                            fixAspect
                            rounded
                            alt={name}
                        />
                    ) : (
                        <RoundedWrapper>
                            <Icon
                                name="settings"
                                alt={`${edit_teams} ${and_players}`}
                                fill={COLORS.transparent}
                            />
                        </RoundedWrapper>
                    )}
                    <Text
                        viewBoxWidth={BASE_WIDTH}
                        viewBoxHeight={37}
                        fontSize="15px"
                    >
                        <P>{id ? firstName : edit_teams}</P>
                        <P>{id ? `${lastName}${suffix}` : and_players}</P>
                    </Text>
                </>
            )}
        </Card>
    );

};

PlayerCard.propTypes = {
    id: PropTypes.number,
    lastName: PropTypes.string,
    name: PropTypes.string,
    as: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.node,
    ]),
    loading: PropTypes.bool,
};

PlayerCard.defaultProps = {
    loading: false,
    id: 0,
    lastName: '',
    name: '',
};

export default PlayerCard;
